import { put, call, takeEvery } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import AuthGateway from '~apis/auth';
import combinedActions from '~redux/combinedActions';

import { TLoginAPIParams } from '~types/feat/auth';
import { GatewayResponseStatus } from '~types/gateway';
import { SagaWatcherReturnType } from '~types/saga';

import navActions from '~utils/navActions';
import Utils from '~utils/functions';
import { ErrorMessages, SuccessMessages } from '~utils/constants';
import { ModuleEnum, NavigationTabsEnum, PermissionsEnum } from '~utils/enums';

export default function* watchLogin(api: AuthGateway): SagaWatcherReturnType {
    yield takeEvery('auth/authLoginAttempt', handleLogin, api);
}

function* handleLogin(api: AuthGateway, data: PayloadAction<TLoginAPIParams>) {
    const { username, password } = data.payload;

    const ACCOUNT_LOCK_ERROR_CODES = [1032, 1033];

    if (!username && !password) {
        yield put(combinedActions.authLoginFailure(ErrorMessages.LoginMissingUserIdAndPasswordError));
        return;
    }

    if (!username) {
        yield put(combinedActions.authLoginFailure(ErrorMessages.LoginMissingUserIdError));
        return;
    }

    if (!password) {
        yield put(combinedActions.authLoginFailure(ErrorMessages.LoginMissingPasswordError));
        return;
    }

    const response = yield* call([api, api.login], { username, password });

    if (response.status === GatewayResponseStatus.Error) {
        const errorMessage = response.message || ErrorMessages.LoginGenericError;

        if (response.code && ACCOUNT_LOCK_ERROR_CODES.includes(Number(response.name))) {
            yield put(combinedActions.authAccountLocked(errorMessage));

            toast.error(errorMessage);

            return;
        }

        yield put(combinedActions.authLoginFailure(errorMessage));

        toast.error(errorMessage);

        return;
    }

    if (response.status === GatewayResponseStatus.Success) {
        const {
            user,
            token,
            exp,
        } = response.data;

        yield call(() => Utils.Auth.storeAuthToken(token));
        yield call(() => Utils.SessionStorage.setItem('authTokenExp', exp));

        yield put(combinedActions.authLoginSuccess(
            {
                authToken: token,
                ...user,
            }
        ));

        const viewAccessModules = user.access.find(item => item.permission === PermissionsEnum.View)?.modules || [];

        yield put(combinedActions.navigationSetCurrentTab(NavigationTabsEnum.Dashboard));

        yield put(combinedActions.authGetUserDetailsAttempt({})); // get current user's details
        yield put(combinedActions.settingGetAPIKeySettingsAttempt()); // get api key
        yield put(combinedActions.settingGetSilverAPIKeySettingsAttempt()); // get silver api key
        yield put(combinedActions.goldGetGoldPricesAttempt());
        yield put(combinedActions.silverGetSilverPricesAttempt());

        yield put(combinedActions.branchGetAllBranchesAttempt()); // get all branches
        yield put(combinedActions.branchGetAllRegionsAttempt()); // get all regions
        yield put(combinedActions.goldGetAllProductsAttempt()); // get all products

        if (viewAccessModules.includes(ModuleEnum.Inventory)) yield put(combinedActions.goldGetAllInventoriesAttempt()); // get all inventories

        yield call(() => toast.success(SuccessMessages.LoginSuccess));
        yield call(navActions.navResetToHome);
    }
}
